import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Icon from '../components/Icon';

function Dashboard() {
  const { user, isDataReadOnly } = useAuth();
  const [stats, setStats] = useState({
    customers: 0,
    staff: 0,
    recentImports: [],
  });
  const [loading, setLoading] = useState(true);
  const [viewedUserData, setViewedUserData] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [customersRes, staffRes, logsRes, profileRes] = await Promise.all([
          axios.get('/api/customers?limit=1'),
          axios.get('/api/staff?limit=1'),
          axios.get('/api/csv/logs'),
          axios.get('/api/auth/profile'),
        ]);

        setStats({
          customers: customersRes.data.pagination?.total || 0,
          staff: staffRes.data.pagination?.total || 0,
          recentImports: (logsRes.data.logs || logsRes.data || []).slice(0, 5),
        });

        // Store the viewed user's data (includes last login for the user being viewed)
        setViewedUserData(profileRes.data.user);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  const formatLastLogin = (lastLogin) => {
    if (!lastLogin) return 'This is your first login';
    const date = new Date(lastLogin);
    return date.toLocaleString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDataCompleteDate = (value) => {
    if (!value) return '';
    return new Date(value).toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  // Use viewed user's data for last login (important when admin views as another user)
  // Don't fall back to user.lastLogin - that would show admin's last login for users who haven't logged in
  const displayLastLogin = viewedUserData ? viewedUserData.lastLogin : null;
  const displayDataCompleteAt = viewedUserData?.dataCompleteAt || null;
  const displayName =
    viewedUserData?.firstName || viewedUserData?.username || user.firstName || user.username;

  return (
    <div className="container-fluid pt-0 mt-0">
      {/* Last Login Banner */}
      <div className="mb-2 py-1">
        <small className="text-muted">
          <i className="bi bi-clock-history me-2"></i>
          Last login: <strong>{formatLastLogin(displayLastLogin)}</strong>
        </small>
      </div>

      {displayDataCompleteAt && (
        <div className="alert alert-success py-2 mb-3" role="status">
          <i className="bi bi-check-circle me-2"></i>
          Data marked complete on <strong>{formatDataCompleteDate(displayDataCompleteAt)}</strong>
          {isDataReadOnly && '. Your account is in read-only mode.'}
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <div className="d-flex align-items-center mb-2">
            <i className="bi bi-speedometer2 me-3 text-info" style={{ fontSize: '2rem' }}></i>
            <h1 className="h3 mb-0 fw-bold">Dashboard</h1>
          </div>
          <p className="text-muted mb-0 small">Welcome back, {displayName}!</p>
        </div>
      </div>

      <div className="row g-4 mb-4">
        <div className="col-md-4">
          <div className="card stats-card h-100">
            <div className="card-body text-center">
              <Icon name="users" className="fs-1 mb-3" />
              <h3 className="text-uppercase small fw-semibold mb-2">Customers</h3>
              <div className="stats-number">{stats.customers.toLocaleString()}</div>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card stats-card h-100">
            <div className="card-body text-center">
              <Icon name="user" className="fs-1 mb-3" />
              <h3 className="text-uppercase small fw-semibold mb-2">Staff Members</h3>
              <div className="stats-number">{stats.staff.toLocaleString()}</div>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card stats-card h-100">
            <div className="card-body text-center">
              <Icon name="documentText" className="fs-1 mb-3" />
              <h3 className="text-uppercase small fw-semibold mb-2">Recent Activity</h3>
              <div className="stats-number">{stats.recentImports.length}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header d-flex align-items-center">
          <Icon name="clipboard" className="me-2" />
          <h5 className="mb-0 fw-semibold">Recent Import/Export Activity</h5>
        </div>
        <div className="card-body">
          {stats.recentImports.length === 0 ? (
            <div className="text-center py-4">
              <Icon name="documentText" className="fs-1 text-muted mb-3" />
              <p className="text-muted mb-0 small">No recent activity</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th className="border-0 small text-uppercase">Operation</th>
                    <th className="border-0 small text-uppercase">Table</th>
                    <th className="border-0 small text-uppercase">Records</th>
                    <th className="border-0 small text-uppercase">Status</th>
                    <th className="border-0 small text-uppercase">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recentImports.map((log) => (
                    <tr key={log.id}>
                      <td className="fw-medium small text-capitalize">{log.operation_type}</td>
                      <td className="small">{log.table_name}</td>
                      <td className="small">{log.records_processed}</td>
                      <td>
                        <span
                          className={`badge ${log.records_failed > 0 ? 'bg-warning' : 'bg-success'}`}
                        >
                          {log.records_failed > 0 ? 'Partial' : 'Success'}
                        </span>
                      </td>
                      <td className="text-muted small">
                        {new Date(log.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header d-flex align-items-center">
          <Icon name="plus" className="me-2" />
          <h5 className="mb-0 fw-semibold">Quick Actions</h5>
        </div>
        <div className="card-body">
          <div className="d-flex gap-2 flex-wrap">
            <a href="/customers" className="btn btn-primary btn-sm">
              <Icon name="users" className="me-1" />
              Manage Customers
            </a>
            <a href="/staff" className="btn btn-primary btn-sm">
              <Icon name="user" className="me-1" />
              Manage Staff
            </a>
            {!isDataReadOnly && (
              <a href="/import-export" className="btn btn-success btn-sm">
                <Icon name="upload" className="me-1" />
                Import/Export Data
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
